import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserPlus, Users, Mail, Clock, RefreshCw, X, LogOut, Copy, Check } from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@/components/ui'
import { LoadingSpinner, EmptyState, ConfirmDialog } from '@/components/shared'
import { MemberCard, InviteMemberModal, EditMemberModal } from '@/components/family'
import { PrimaryCaregiverOnly } from '@/components/auth'
import {
  useFamily,
  useFamilyMembers,
  useFamilyInvitations,
  usePermissions,
  getRoleDisplayName,
} from '@/hooks'
import type { FamilyMember, Invitation } from '@/types'

// Separate component to handle invitation rendering with Date.now()
function InvitationCard({
  invitation,
  onResend,
  onCancel,
  onCopy,
  isCopied,
  isResending,
  isCancelling,
}: {
  invitation: Invitation
  onResend: () => void
  onCancel: () => void
  onCopy: () => void
  isCopied: boolean
  isResending: boolean
  isCancelling: boolean
}) {
  const [now, setNow] = useState(() => Date.now())

  // Update time every minute to keep expiry status fresh
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000)
    return () => clearInterval(interval)
  }, [])

  const expiresAt = new Date(invitation.expires_at)
  const isExpiringSoon = expiresAt.getTime() - now < 7 * 24 * 60 * 60 * 1000 // 7 days

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
          <Mail className="h-5 w-5 text-gray-500" />
        </div>
        <div>
          <div className="font-medium text-gray-900">{invitation.email}</div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Badge variant="secondary">{getRoleDisplayName(invitation.role)}</Badge>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Expires {expiresAt.toLocaleDateString()}
            </span>
            {isExpiringSoon && <Badge variant="warning">Expiring soon</Badge>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {invitation.invitation_link && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCopy}
            aria-label={`Copy invitation link for ${invitation.email}`}
          >
            {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="ml-2 hidden sm:inline">{isCopied ? 'Copied' : 'Copy link'}</span>
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onResend} disabled={isResending}>
          <RefreshCw className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isCancelling}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export function FamilyPage() {
  const navigate = useNavigate()
  const { selectedFamily, selectedFamilyId, setSelectedFamilyId, families } = useFamily()
  const { role, isPrimaryCaregiver } = usePermissions()
  const {
    members,
    isLoading: isMembersLoading,
    updateMember,
    removeMember,
    leaveFamily,
    isUpdating,
    isRemoving,
    isLeaving,
  } = useFamilyMembers(selectedFamilyId)
  const {
    invitations,
    isLoading: isInvitationsLoading,
    inviteMember,
    cancelInvitation,
    resendInvitation,
    isInviting,
    isCancelling,
    isResending,
  } = useFamilyInvitations(selectedFamilyId)

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<FamilyMember | null>(null)
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false)
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null)

  const pendingInvitations = invitations.filter((i) => i.status === 'PENDING')
  const isLoading = isMembersLoading || isInvitationsLoading

  const handleRemoveMember = async () => {
    if (!memberToRemove) return
    try {
      await removeMember(memberToRemove.id)
      setMemberToRemove(null)
    } catch {
      // Error handling done in hook
    }
  }

  const handleCancelInvitation = async () => {
    if (!invitationToCancel) return
    try {
      await cancelInvitation(invitationToCancel.id)
      setInvitationToCancel(null)
    } catch {
      // Error handling done in hook
    }
  }

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation(invitationId)
    } catch {
      // Error handling done in hook
    }
  }

  const handleCopyInvitationLink = async (invitation: Invitation) => {
    if (!invitation.invitation_link) return

    await navigator.clipboard.writeText(invitation.invitation_link)
    setCopiedInvitationId(invitation.id)
    window.setTimeout(() => {
      setCopiedInvitationId((current) => (current === invitation.id ? null : current))
    }, 2000)
  }

  const handleLeaveFamily = async () => {
    try {
      await leaveFamily()
      setShowLeaveConfirm(false)
      // Switch to another family or go to onboarding if no families left
      const remainingFamilies = families.filter((f) => f.id !== selectedFamilyId)
      if (remainingFamilies.length > 0) {
        setSelectedFamilyId(remainingFamilies[0].id)
      } else {
        navigate('/onboarding')
      }
    } catch {
      // Error handling done in hook
    }
  }

  if (!selectedFamily) {
    return (
      <div className="flex h-64 items-center justify-center">
        <EmptyState
          icon={Users}
          title="No family selected"
          description="Please select a family to manage"
        />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{selectedFamily.name}</h1>
          <p className="text-gray-500">
            {members.length} member{members.length !== 1 ? 's' : ''} in this family
          </p>
        </div>
        <div className="flex gap-2">
          {!isPrimaryCaregiver && (
            <Button variant="outline" onClick={() => setShowLeaveConfirm(true)}>
              <LogOut className="mr-2 h-4 w-4" />
              Leave Family
            </Button>
          )}
          <PrimaryCaregiverOnly>
            <Button onClick={() => setIsInviteModalOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          </PrimaryCaregiverOnly>
        </div>
      </div>

      {/* Members list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Family Members
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {members.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No members yet"
              description="Invite family members to get started"
              action={
                isPrimaryCaregiver
                  ? {
                      label: 'Invite Member',
                      onClick: () => setIsInviteModalOpen(true),
                    }
                  : undefined
              }
            />
          ) : (
            members.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                currentUserRole={role}
                onEdit={isPrimaryCaregiver ? setEditingMember : undefined}
                onRemove={isPrimaryCaregiver ? setMemberToRemove : undefined}
                isRemoving={isRemoving}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Pending invitations (only for primary caregivers) */}
      <PrimaryCaregiverOnly>
        {pendingInvitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Pending Invitations
                <Badge variant="secondary">{pendingInvitations.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <InvitationCard
                  key={invitation.id}
                  invitation={invitation}
                  onResend={() => handleResendInvitation(invitation.id)}
                  onCancel={() => setInvitationToCancel(invitation)}
                  onCopy={() => handleCopyInvitationLink(invitation)}
                  isCopied={copiedInvitationId === invitation.id}
                  isResending={isResending}
                  isCancelling={isCancelling}
                />
              ))}
            </CardContent>
          </Card>
        )}
      </PrimaryCaregiverOnly>

      {/* Invite modal */}
      <InviteMemberModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={inviteMember}
        isInviting={isInviting}
      />

      {/* Edit member modal */}
      <EditMemberModal
        open={!!editingMember}
        member={editingMember}
        onClose={() => setEditingMember(null)}
        onUpdate={updateMember}
        isUpdating={isUpdating}
      />

      {/* Remove member confirmation */}
      <ConfirmDialog
        open={!!memberToRemove}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
        onConfirm={handleRemoveMember}
        title="Remove family member?"
        description={`Are you sure you want to remove ${memberToRemove?.user.full_name} from the family? They will lose access to all family data.`}
        confirmLabel="Remove"
        variant="destructive"
        loading={isRemoving}
      />

      {/* Cancel invitation confirmation */}
      <ConfirmDialog
        open={!!invitationToCancel}
        onOpenChange={(open) => !open && setInvitationToCancel(null)}
        onConfirm={handleCancelInvitation}
        title="Cancel invitation?"
        description={`Are you sure you want to cancel the invitation to ${invitationToCancel?.email}?`}
        confirmLabel="Cancel Invitation"
        variant="destructive"
        loading={isCancelling}
      />

      {/* Leave family confirmation */}
      <ConfirmDialog
        open={showLeaveConfirm}
        onOpenChange={setShowLeaveConfirm}
        onConfirm={handleLeaveFamily}
        title="Leave this family?"
        description={`Are you sure you want to leave ${selectedFamily.name}? You will lose access to all family data and need a new invitation to rejoin.`}
        confirmLabel="Leave Family"
        variant="destructive"
        loading={isLeaving}
      />
    </div>
  )
}
